import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import tasksService from "../../../services/tasks.service";
import { Box, Paper } from "@mui/material";
import styles from "./Task.module.scss";
import { useTranslation } from "react-i18next";
import SpinnerArea from "../../../components/SpinnerArea";
import MainContext from "../../../context/mainContext";
import TaskHeader from "./TaskHeader";
import LegalReview from "./taskTypes/LegalReview";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PeerReviewApproval from "./taskTypes/PeerReviewApproval";
import PeerReview from "./taskTypes/PeerReview";
import { ITaskHeader } from "../../../models/task.model";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ColleagueFeedback from "./taskTypes/ColleagueFeedback";
import DocumentApproval from "./taskTypes/DocumentApproval";
import CancelIcon from "@mui/icons-material/Cancel";
import DOIAssigment from "./taskTypes/DOIAssigment";
import DeleteApproval from "./taskTypes/DeleteApproval";

const TaskCompleted: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Box className={styles.taskCompleted}>
      <h2>{t("taskCompleted")}</h2>
      <CheckCircleIcon />
    </Box>
  );
};

const TaskCancelled: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Box className={styles.taskCancelled}>
      <h2>{t("taskCancelled")} </h2>
      <CancelIcon />
    </Box>
  );
};

const TaskView: React.FC = () => {
  const { taskId } = useParams();
  const { setBanner, service } = useContext(MainContext);
  const [taskHeader, setTaskHeader] = useState<ITaskHeader>();
  const [loading, setLoading] = useState(false);
  const [unauthorized, setUnauthorized] = useState(false);

  const { t } = useTranslation();
  const [sharedLoading, setSharedLoading] = useState(false);

  useEffect(() => {
    setBanner("rocky-peak-sunset.png");
  });

  useEffect(() => {
    service(() => tasksService.header(taskId!), {
      callback: (data) => {
        if (data) {
          setTaskHeader(data);
        }
      },
      loader: setLoading,
      handleError: (error: any) => {
        if ("response" in error && error.response.status === 403) {
          setUnauthorized(true);
        }
      },
    });
  }, [taskId]);

  const BuildTaskContent = () => {
    if (taskHeader?.status === "Completed") return <TaskCompleted />;

    if (taskHeader?.status === "Cancelled") return <TaskCancelled />;

    switch (taskHeader?.type) {
      case "LegalReview":
        return <LegalReview taskHeader={taskHeader} />;
      case "PeerReview":
        return <PeerReview taskHeader={taskHeader} />;
      case "PeerReviewApproval":
        return <PeerReviewApproval taskHeader={taskHeader} />;
      case "ColleagueReview":
        return <ColleagueFeedback taskHeader={taskHeader} />;
      case "DOIAssigment":
        return <DOIAssigment taskHeader={taskHeader} />;
      case "FinalVersionApproval":
      case "FinalVersionApprovalAuthor":
      case "DraftApproval":
        return <DocumentApproval taskHeader={taskHeader} />;
      case "DeleteApproval":
        return <DeleteApproval taskHeader={taskHeader} />;
      default:
        return <>not implemented</>;
    }
  };

  return (
    <SpinnerArea loading={loading || sharedLoading}>
      <Box className={styles.taskView}>
        <div className={styles.taskTitle}>
        <h2>{t("publication")} <strong>{t("publicationHUB")}</strong></h2>
        </div>
        <Paper className={styles.main}>
          {taskHeader && (
            <>
              <TaskHeader taskHeader={taskHeader} loader={setSharedLoading} />
              {BuildTaskContent()}
            </>
          )}
          {unauthorized && (
            <h2 className={styles.unauthorizedMessage}>
              <ErrorOutlineIcon /> {t("unauthorizedTask")}
            </h2>
          )}
        </Paper>
      </Box>
    </SpinnerArea>
  );
};

export default TaskView;
